import { converterPrecoUnidade, type LinhaMapeada } from './validacao'
import { converterNumeroBR } from '../lib/textoBR'
import { formatarMoeda } from '../lib/formatos'

export type TipoAvisoParidade = 'precos_invertidos' | 'avulso_nao_mapeado' | 'fator_nao_mapeado'

export interface AvisoParidade {
  tipo: TipoAvisoParidade
  codigo?: string
  quantos?: number
  texto: string
}

// So inversao GROSSEIRA dispara. Ela decidiu em 28/08 que os dois precos podem
// divergir de verdade (a fabrica cobra caro para quebrar caixa), entao qualquer
// corte apertado transformaria decisao de negocio em alarme. Colunas trocadas
// erram por ordem de grandeza: com fator 20, dao 400x.
const FATOR_DE_ALARME = 5

// Coluna que a pessoa NAO mapeou nem existe em `campos`: `aplicarMapa`
// (validacao.ts) so escreve as chaves que o mapa cita. Mapeada e com a celula
// vazia, a chave existe valendo ''. A diferenca nao e detalhe -- ela decide a
// FRASE do aviso, e mandar mapear o que ja esta mapeado poe a pessoa a procurar
// um erro que nao existe. Ha teste provando as duas formas.
function colunaMapeada(linhas: readonly LinhaMapeada[], campo: string): boolean {
  return linhas.some((l) => campo in l.campos)
}

// "1 produtos" e o tipo de erro que faz a pessoa desconfiar do resto do aviso.
// A contagem vem colada no substantivo, entao pluralizar tem de ser feito aqui e
// nao no rotulo.
function conta(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural
}

// No maximo TRES, como `agruparErros` ja faz com os erros de linha: a pessoa
// precisa de onde comecar a conferir, nao da lista inteira. A quantidade, essa
// sim, e sempre o total.
const MAX_EXEMPLOS = 3

function listarCodigos(codigos: readonly string[]): string {
  const mostrados = codigos.slice(0, MAX_EXEMPLOS)
  if (mostrados.length === 1) return mostrados[0]
  return `${mostrados.slice(0, -1).join(', ')} e ${mostrados[mostrados.length - 1]}`
}

// Um produto que caiu na conferencia, guardado para virar UM aviso no fim -- e
// nao um aviso por produto.
interface Invertido {
  codigo: string
  avulso: number
  fator: number
  esperado: number
  preco: number
}

export function conferirParidade(linhas: readonly LinhaMapeada[]): AvisoParidade[] {
  const avisos: AvisoParidade[] = []
  // ⚠ UMA PASSADA SO, E SEM `continue` ANTES DE CONTAR. Ate 29/08 o laco
  // descartava a linha logo no `fator <= 1`, e por isso a segunda direcao era
  // impossivel: sem fator, a linha nem chegava a ser contada, e quem mapeava o
  // preco avulso e esquecia o QTD CX perdia o recurso inteiro em silencio.
  // Contamos as duas faltas, e nao os totais: o aviso e PROPORCIONAL. Medir
  // "existe pelo menos um avulso" calava o aviso das outras 499 linhas.
  let semAvulso = 0
  let semFator = 0
  // ⛔ NAO E UM `push` POR PRODUTO, e por medicao: com as duas colunas trocadas,
  // o laco antigo gerava UM aviso por linha -- 500 avisos de ~166 caracteres,
  // ~83.000 caracteres de parede numa tela de 360px, e o representante sem achar
  // o botao de continuar. Junta aqui, e vira UM aviso depois do laco, no molde
  // que `agruparErros` ja usa para os erros de linha.
  const invertidos: Invertido[] = []

  for (const l of linhas) {
    const fatorTexto = l.campos.unidades_por_embalagem ?? ''
    // Fator declarado, ainda que 1, e OPINIAO da pessoa: os 19 produtos da
    // MAVALERIO com fator 1 mapearam a coluna e a preencheram. So a AUSENCIA de
    // valor conta como "faltou o fator".
    const fatorPreenchido = fatorTexto.trim() !== ''
    const fator = converterNumeroBR(fatorTexto) ?? 0
    const preco = converterNumeroBR(l.campos.preco ?? '')
    // ⭐ R12: a MESMA leitura da validacao, e nao uma copia. `0` na coluna do
    // avulso quer dizer "este produto nao vende unidade solta" -- ate 29/08 as
    // duas camadas discordavam justamente aqui, e foi assim que o zero virou
    // preco valido de um lado e "sem avulso" do outro.
    const avulso = converterPrecoUnidade(l.campos.preco_unidade ?? '')
    // Fator 1 nao tem paridade a conferir: a embalagem E a unidade.
    const temEmbalagem = fator > 1
    const temAvulso = typeof avulso === 'number'
    // ⭐⭐ CELULA VAZIA E LACUNA; CELULA COM ZERO E RESPOSTA. Consequencia direta
    // da R12: `0` quer dizer "este produto nao vende unidade solta", entao
    // conta-lo como "faltou o preco avulso" seria cobrar dela uma resposta que
    // ela ja deu -- e a frase que a tela mostrava ("a coluna foi mapeada, mas
    // veio VAZIA nessas linhas") era falsa. Texto ilegivel e negativo nao
    // chegam ate aqui: `validarProdutos` reprova a linha antes.
    const avulsoRespondido = (l.campos.preco_unidade ?? '').trim() !== ''

    if (temEmbalagem && !temAvulso && !avulsoRespondido) semAvulso++
    if (temAvulso && !fatorPreenchido) semFator++

    if (!temEmbalagem || typeof avulso !== 'number') continue
    if (preco === null || preco === 0) continue

    const esperado = avulso * fator
    const razao = esperado > preco ? esperado / preco : preco / esperado
    if (razao >= FATOR_DE_ALARME) {
      invertidos.push({ codigo: l.campos.codigo, avulso, fator, esperado, preco })
    }
  }

  // O aviso agrupado. Vem primeiro porque e o unico que fala do arquivo INTEIRO.
  // ⚠ Os numeros saem em portugues (`formatarMoeda`): a tela toda mostra
  // `R$ 121,16`, e ate 29/08 este aviso mostrava `121.16` no meio da frase. O
  // FATOR nao passa por `formatarMoeda` -- ele e quantidade, e virar `R$ 20,00`
  // seria inventar um preco que nao existe.
  if (invertidos.length > 0) {
    const d = invertidos[0]
    const quantos = invertidos.length
    const abertura = quantos === 1
      ? '1 produto tem o preço avulso e o preço da embalagem muito diferentes entre si.'
      : `${quantos} produtos têm o preço avulso e o preço da embalagem muito diferentes `
        + `entre si — por exemplo ${listarCodigos(invertidos.map((i) => i.codigo))}.`
    avisos.push({
      tipo: 'precos_invertidos',
      // O codigo do primeiro exemplo. Serve de chave estavel na tela e mantem o
      // aviso ancorado num produto que a pessoa pode ir conferir.
      codigo: d.codigo,
      quantos,
      texto:
        `${abertura} No código ${d.codigo}, o preço avulso é ${formatarMoeda(d.avulso)} e a `
        + `embalagem tem ${d.fator} unidades, o que daria ${formatarMoeda(d.esperado)} — mas o `
        + `preço da embalagem está ${formatarMoeda(d.preco)}. `
        + 'As duas colunas de preço podem estar trocadas.',
    })
  }

  // As duas direcoes da mesma pergunta, como a spec pede ("mapeou fator e nao
  // mapeou preco avulso OU O CONTRARIO"). Cada uma tem duas frases, porque a
  // condicao mede VALORES e nao colunas: dizer "nenhuma coluna foi mapeada"
  // para quem mapeou e deixou celulas vazias e mandar consertar o que ja esta
  // certo.
  if (semAvulso > 0) {
    const n = semAvulso
    // Tom tranquilo (decisao dela, 15/10, opcao b): o caso mais comum e CERTO
    // -- produto vendido so em caixa fechada -- disparava este aviso sempre,
    // soando como erro. Continua avisando, mas diz que esta tudo bem.
    const sujeito = `${n} ${conta(n, 'produto vem', 'produtos vêm')}`
    avisos.push({
      tipo: 'avulso_nao_mapeado',
      quantos: n,
      texto: colunaMapeada(linhas, 'preco_unidade')
        ? `${sujeito} em embalagem com mais de uma unidade e a coluna de preço avulso veio ` +
          `vazia ${conta(n, 'nessa linha', 'nessas linhas')}. Tudo certo se a fábrica só ` +
          'vende a embalagem fechada.'
        : `${sujeito} em embalagem com mais de uma unidade e a planilha não tem coluna de ` +
          'preço avulso. Tudo certo se a fábrica só vende a embalagem fechada; se ela vende ' +
          'avulso, volte e mapeie a coluna.',
    })
  }
  if (semFator > 0) {
    const n = semFator
    const sujeito = `${n} ${conta(n, 'produto tem', 'produtos têm')}`
    avisos.push({
      tipo: 'fator_nao_mapeado',
      quantos: n,
      texto: colunaMapeada(linhas, 'unidades_por_embalagem')
        ? `${sujeito} preço avulso e ${conta(n, 'ficou', 'ficaram')} sem quantas unidades ` +
          'vêm na embalagem: a coluna foi mapeada, mas veio vazia ' +
          `${conta(n, 'nessa linha', 'nessas linhas')}. Sem ela o app não monta a caixa; ` +
          'confira a planilha.'
        : `${sujeito} preço avulso e nenhuma coluna de unidades por embalagem ` +
          'foi mapeada. Sem ela o app não sabe quantas unidades vêm na caixa; volte e mapeie.',
    })
  }
  return avisos
}
